/**
 * Website role names (the `name` column of `roles`, see seed.sql) and the
 * role sets the authorization gates decide on: defined ONCE here so neither
 * the API nor the web app spells a role inline. Case-sensitive, compared
 * against the user's role name. Moved from the API's security/roles.ts and
 * the web's hand copies by CONFIG CF-4.
 *
 * The sets differ on purpose; pick by what the rule means, not by what
 * looks close:
 *
 *   ADMIN_ROLES               Super Admin, Admin
 *   MANAGER_PLUS_ROLES        Super Admin, Admin, Manager
 *   RESEARCH_TIER_ROLES       Research
 *   MANAGER_OR_RESEARCH_ROLES Super Admin, Admin, Manager, Research
 *   RESEARCH_OR_ADMIN_ROLES   Super Admin, Admin, Research
 *
 * Who skips permission checks is a separate rule, skipsPermissionChecks below.
 */
import { isAigStaff } from '../security/super-admin-whitelist.js';
export const SUPER_ADMIN_ROLE = 'Super Admin';
export const ADMIN_ROLE = 'Admin';
export const MANAGER_ROLE = 'Manager';
export const RESEARCH_ROLE = 'Research';
export const BROKER_ROLE = 'Broker';
/** The self-serve role whose creates are auto-flagged for review (user-role-review-flag). */
export const USER_ROLE = 'User';
/** Listings L0-1: runs listing agreements and their tasks; read-only on property data (Matt, 2026-10-02). */
export const BROKERAGE_ASSISTANT_ROLE = 'Brokerage Assistant';
/**
 * M64: HR roles. They carry only feature keys (feature:hr_directory, feature:hr_requests; the
 * Director also feature:hr_protected_fields) and sit in NO role set below, so they outrank nobody:
 * roleRank gives them 0 and no gate lists them. The one thing they can do that an Admin cannot is
 * hold feature:hr_protected_fields (M63's exception).
 */
export const HR_DIRECTOR_ROLE = 'HR Director';
export const HR_ASSISTANT_ROLE = 'HR Assistant';
/**
 * Property management roles. Like the HR roles they sit in no role set; the web reads the
 * Property Manager name for one UI default (M266: Managed properties opens on their own).
 */
export const PROPERTY_MANAGER_ROLE = 'Property Manager';
export const PROPERTY_ASSISTANT_ROLE = 'Property Assistant';
/** Admin tier: data-sources admin guard, requireAdminRole, the admin-only settings pages. */
export const ADMIN_ROLES = new Set([SUPER_ADMIN_ROLE, ADMIN_ROLE]);
/** Manager-and-above (the legacy confidentiality-write set; reference-data defs). */
export const MANAGER_PLUS_ROLES = new Set([SUPER_ADMIN_ROLE, ADMIN_ROLE, MANAGER_ROLE]);
/** Research tier. */
export const RESEARCH_TIER_ROLES = new Set([RESEARCH_ROLE]);
/**
 * Manager+ OR Research: who may set confidentiality (F12 full unlock) and who
 * may write anyone's property_assignments (research does the slot-filling).
 */
export const MANAGER_OR_RESEARCH_ROLES = new Set([...MANAGER_PLUS_ROLES, ...RESEARCH_TIER_ROLES]);
/**
 * The tiers the visibility predicates (fn_user_can_see_*, fn_user_can_edit_report)
 * already return TRUE for — the comp gates skip the RPC round trip for them.
 * Manager is NOT in it: managers go through the predicate like brokers.
 */
export const RESEARCH_OR_ADMIN_ROLES = new Set([SUPER_ADMIN_ROLE, ADMIN_ROLE, RESEARCH_ROLE]);
/**
 * Who skips permission checks (the feature:*, widget:* and other grants a role holds): a user
 * whose primary role is Super Admin, the recovery role that can always reopen the Feature Access
 * matrix, and AIG staff (isAigStaff). Everyone else, Admin included, holds what their roles'
 * grants say. The API's permission middleware and in-handler checks and the web's access hooks
 * all decide by this one rule, so the app never shows what the server refuses. It is not an
 * admin-role test: role gates (ADMIN_ROLES, requireAdminRole) still let Admin in.
 *
 * `role` is the primary role name (request.user.role on the API, role.name in the web);
 * `isAdmin` is the any-role admin flag both carry. Exact-grant-only keys
 * (EXACT_GRANT_ONLY_FEATURES) are held by AIG staff and their grant holders only; callers that
 * check one test that first.
 */
export function skipsPermissionChecks(user) {
    if (!user)
        return false;
    return user.role === SUPER_ADMIN_ROLE || isAigStaff({ email: user.email, isAdmin: user.isAdmin === true });
}
//# sourceMappingURL=roles.js.map